import { toggleDataAttribute } from './navigation';

type BillingPeriod = 'monthly' | 'yearly';

const pricingSections =
  document.querySelectorAll<HTMLElement>('[data-pricing-section]');

function getDatasetPrice(element: HTMLElement, billing: BillingPeriod) {
  return billing === 'yearly'
    ? element.dataset.priceYearly || element.dataset.priceMonthly
    : element.dataset.priceMonthly;
}

function parsePrice(price?: string) {
  if (!price) return null;

  const parsedPrice = Number(price);

  return Number.isNaN(parsedPrice) ? null : parsedPrice;
}

function formatPrice(price?: string) {
  const parsedPrice = parsePrice(price);
  if (parsedPrice === null) return '';

  return `€ ${parsedPrice.toLocaleString('nl-NL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

pricingSections.forEach(function (section) {
  const billingButtons = section.querySelectorAll<HTMLButtonElement>(
    '[data-billing-option]',
  );
  const billingToggle = section.querySelector<HTMLElement>(
    '[data-pricing-billing-toggle]',
  );
  const defaultBillingPeriod = billingToggle?.dataset.billingPeriod;
  let billingPeriod: BillingPeriod =
    defaultBillingPeriod === 'monthly' ? 'monthly' : 'yearly';
  const billingIndicator = section.querySelector<HTMLElement>(
    '[data-billing-indicator]',
  );
  const plans = section.querySelectorAll<HTMLElement>('[data-plan]');

  function setActiveBillingButton() {
    if (billingToggle) billingToggle.dataset.billingPeriod = billingPeriod;
    if (billingIndicator) billingIndicator.dataset.billingPeriod = billingPeriod;

    billingButtons.forEach(function (button) {
      const isActive = button.dataset.billingOption === billingPeriod;

      button.dataset.active = String(isActive);
      button.setAttribute('aria-pressed', String(isActive));
    });
  }

  function updatePlan(plan: HTMLElement) {
    const selectedOption = plan.querySelector<HTMLElement>(
      '[data-pricing-option][data-selected="true"]',
    );
    const priceDisplay = plan.querySelector<HTMLElement>('[data-price-display]');
    const periodDisplay = plan.querySelector<HTMLElement>('[data-price-period]');
    const switchEl = plan.querySelector<HTMLInputElement>(
      '[data-whitelabel-switch]',
    );

    if (!priceDisplay) return;

    const priceSource = selectedOption || priceDisplay;
    const price = getDatasetPrice(priceSource, billingPeriod);
    const formattedPrice = formatPrice(price);

    priceDisplay.textContent = formattedPrice || priceDisplay.dataset.priceText || '';

    if (periodDisplay) {
      if (!formattedPrice) {
        periodDisplay.textContent = '';
      } else if (billingPeriod === 'yearly') {
        const parsedPrice = parsePrice(price);
        const annualPrice = parsedPrice === null
          ? ''
          : formatPrice(String(parsedPrice * 12));

        periodDisplay.textContent = annualPrice
          ? (periodDisplay.dataset.periodYearlyTemplate || '').replace(
              '{price}',
              annualPrice,
            )
          : periodDisplay.dataset.periodMonthly || '';
      } else {
        periodDisplay.textContent = periodDisplay.dataset.periodMonthly || '';
      }
    }

    if (switchEl) {
      const parsedPrice = parsePrice(price);
      if (parsedPrice !== null) switchEl.checked = parsedPrice > 0;
    }
  }

  plans.forEach(function (plan) {
    const dropdownToggle =
      plan.querySelector<HTMLButtonElement>('#pricing-dropdown');

    if (dropdownToggle) {
      const ul = plan.querySelector<HTMLElement>('#pricing-dropdown-ul');
      const selectedSpan = dropdownToggle.querySelector('.selected-option');
      const icon = dropdownToggle.querySelector('i');

      if (!ul || !selectedSpan || !icon) return;

      const firstOption = ul.querySelector<HTMLElement>(
        '[data-pricing-option]',
      );
      if (firstOption) firstOption.dataset.selected = 'true';

      function toggleDropdown() {
        toggleDataAttribute(ul, 'data-open');
        toggleDataAttribute(icon, 'data-open');
      }

      function handleSelect(e: Event) {
        const target = e.target as HTMLElement;

        if (
          target &&
          target.matches('[data-pricing-option]') &&
          (e instanceof MouseEvent || e instanceof KeyboardEvent)
        ) {
          e.preventDefault();
          selectedSpan.textContent = target.textContent;
          ul.querySelectorAll<HTMLElement>('[data-pricing-option]').forEach(
            function (option) {
              option.dataset.selected = String(option === target);
            },
          );
          updatePlan(plan);
          toggleDropdown();
        }
      }

      dropdownToggle.addEventListener('click', function (e) {
        e.stopPropagation();
        toggleDropdown();
      });

      ul.addEventListener('click', handleSelect);
      ul.addEventListener('keydown', function (e) {
        if (['Enter', ' ', 'Spacebar'].includes(e.key)) handleSelect(e);
      });
    }

    updatePlan(plan);
  });

  billingButtons.forEach(function (button) {
    button.addEventListener('click', function () {
      const nextBillingPeriod = button.dataset.billingOption;
      if (
        nextBillingPeriod !== 'monthly' &&
        nextBillingPeriod !== 'yearly'
      ) {
        return;
      }

      billingPeriod = nextBillingPeriod;
      setActiveBillingButton();
      plans.forEach(updatePlan);
    });
  });

  setActiveBillingButton();
});
